import type { AssetTypes } from 'js/src/client';
import type { UploadModalContext } from 'js/src/data/modals/modals-types';
import { MODAL_IDS } from 'js/src/data/modals/modals-types';
import type { RootState } from 'js/src/libs/RootState';

export function getTopModalId(state: RootState): MODAL_IDS | undefined {
    const stack = state.modals.visible_stack;
    return stack[stack.length - 1];
}

export function isVideoNameEditingModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.VIDEO_NAME_EDITING;
}

export function isReturnToAnimationEditorModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.RETURN_TO_ANIMATION_EDITOR;
}

export function isProjectDeleteModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.PROJECT_DELETE;
}

export function isProjectCreateModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.PROJECT_CREATE;
}

export function isVideoListMembersModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.VIDEO_LIST_MEMBERS_UPDATE;
}

export function isTeamMemberInvitationModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.TEAM_MEMBER_INVITATION_MODAL;
}

export function isVideoPreviewModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.VIDEO_PREVIEW;
}

export function isFeatureLockModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.FEATURE_LOCK;
}

export function isSubmitFeedbackModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.SUBMIT_FEEDBACK;
}

export function isPremiumMediaUpgradeModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.PREMIUM_MEDIA_UPGRADE;
}

export function getUploadSessionId(state: RootState): string | null {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return null;
        return context.uploadSessionId;
    }
    return null;
}

export function getUploadConfirmedCallback(
    state: RootState,
): ((uploadPromise: Promise<string[]>) => void) | null {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return null;
        return context.uploadConfirmedCallback || null;
    }
    return null;
}

export function getUploadAssetContainerTypes(state: RootState) {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return null;
        return context.assetContainerTypes;
    }
    return null;
}

export function getConstrainToBackgroundAssetType(state: RootState): AssetTypes | undefined {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return undefined;
        const constrainToBackgroundAssetType = context.constrainToBackgroundAssetType;
        return constrainToBackgroundAssetType || undefined;
    }
    return undefined;
}

export function getConstrainToSingleFile(state: RootState): boolean | null {
    if (isUploadModalShowing(state)) {
        const context = getModalContext<UploadModalContext>(state);
        if (!context) return null;
        return context.constrainToSingleFile || null;
    }
    return null;
}

export function isUploadModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.UPLOAD_MEDIA;
}

export function isLoginRequiredShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.LOGIN_REQUIRED_FOR_COMMENTING;
}

export function isCropModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.CROP;
}

export function isTrimModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.TRIM;
}

export function isInsertTrimModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.INSERT_TRIM;
}

export function isApprovalStatusModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.APPROVAL_STATUS;
}

export function isCreateBlueprintFromVideoModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.CREATE_BLUEPRINT_FROM_VIDEO;
}

export function isTranscriptGeneratedModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.TRANSCRIPT_GENERATED;
}

export function isRemoveTeamMemberModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.REMOVE_TEAM_MEMBER;
}

export function isUserSettingsModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.USER_SETTINGS;
}

export function isRecordYourselfModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.RECORD_YOURSELF;
}

export function isRecordYourScreenModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.RECORD_YOUR_SCREEN;
}

export function isRecordVideoSelectionModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.RECORD_VIDEO_SELECTION;
}

export function isGenericErrorModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.GENERIC_ERROR;
}

export function isVoicedrivenSceneCreationModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.VOICEDRIVEN_SCENE_CREATION;
}

export function isAIVoiceSelectionModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.AI_VOICE_SELECTION;
}

export function isAIAvatarVoicePresetSelectionModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.AI_AVATAR_VOICE_PRESET_SELECTION;
}

export function isEditPronunciationModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.EDIT_PRONUNCIATION;
}

export function isMediaDetailsModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.MEDIA_DETAILS;
}

export function isGenerativeMediaDetailsModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.GENERATIVE_MEDIA_DETAILS;
}

export function isDeletePronunciationModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.DELETE_PRONUNCIATION;
}

export function isTemplatePickerModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.TEMPLATE_PICKER;
}

export function isSketchBlueprintsModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.SKETCH_BLUEPRINTS;
}

export function isCreateBlueprintModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.CREATE_BLUEPRINT;
}

export function isRenameFolderModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.RENAME_FOLDER;
}

export function isDeleteFolderModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.DELETE_FOLDER;
}

export function getModalContext<T extends object>(state: RootState) {
    const stack = state.modals.visible_stack_context;
    const context = stack[stack.length - 1];
    if (context) {
        return context as Readonly<T>;
    }
    return null;
}

export function isFeatureFlagsManagementModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.FEATURE_FLAGS_MANAGEMENT;
}

export function isSoundEffectSelectionModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.SOUND_EFFECT_SELECTION;
}

export function isVoiceCloningModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.VOICE_CLONING;
}

export function isVoiceEditModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.VOICE_EDIT;
}

export function isUseCasePreviewModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.USE_CASE_PREVIEW;
}

export function isNarrationStyleModalShowing(state: RootState): boolean {
    return getTopModalId(state) === MODAL_IDS.NARRATION_STYLE;
}
